import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Upload, Video, Image, Music, X, Check, Loader2 } from 'lucide-react';
import { useDropzone } from 'react-dropzone';

interface ProjectFile {
  id: string;
  name: string;
  fileType?: 'video' | 'audio' | 'image';
  thumbnailUrl?: string;
  s3Key?: string;
  s3Url?: string;
  url?: string;
  duration?: number;
}

interface TemplateAssetPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (asset: { name: string; s3Key: string; url: string; duration?: number }) => void;
  assetType: 'video' | 'image' | 'audio';
  title: string;
  projectFiles: ProjectFile[];
}

export function TemplateAssetPicker({
  isOpen,
  onClose,
  onSelect,
  assetType,
  title,
  projectFiles,
}: TemplateAssetPickerProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Filter project files by type
  const filteredFiles = projectFiles.filter(file => {
    if (assetType === 'video') return file.fileType === 'video';
    if (assetType === 'image') return file.fileType === 'image';
    if (assetType === 'audio') return file.fileType === 'audio';
    return false;
  });

  const handleFileSelect = (file: ProjectFile) => {
    onSelect({
      name: file.name,
      s3Key: file.s3Key || '',
      url: file.url || file.s3Url || '',
      duration: file.duration,
    });
    onClose();
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setIsUploading(true);
    setUploadProgress(0);

    // Determine content type - file.type can be empty for some files
    const getContentType = (file: File): string => {
      if (file.type) return file.type;
      const ext = file.name.split('.').pop()?.toLowerCase();
      const mimeTypes: Record<string, string> = {
        'png': 'image/png',
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'gif': 'image/gif',
        'webp': 'image/webp',
        'mp4': 'video/mp4',
        'mov': 'video/quicktime',
        'webm': 'video/webm',
        'mp3': 'audio/mpeg',
        'wav': 'audio/wav',
        'aac': 'audio/aac',
      };
      return mimeTypes[ext || ''] || 'application/octet-stream';
    };

    const contentType = getContentType(file);
    console.log('Uploading file:', { name: file.name, type: file.type, contentType });

    // Get auth token from multiple possible sources (same pattern as other components)
    const getAuthToken = (): string | null => {
      // Try cookies first
      const cookieToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('authToken=') || row.startsWith('token='))
        ?.split('=')[1];
      if (cookieToken) return cookieToken;
      // Then localStorage
      return localStorage.getItem('authToken') || localStorage.getItem('token');
    };

    const authToken = getAuthToken();
    console.log('Auth token available:', !!authToken);

    try {
      // Get upload URL from backend
      const response = await fetch('/api/get-upload-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
        },
        credentials: 'include', // Include cookies
        body: JSON.stringify({
          fileName: file.name,
          fileType: contentType,
          ephemeral: false, // Permanent storage for templates
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to get upload URL:', response.status, errorText);
        throw new Error(`Failed to get upload URL: ${response.status}`);
      }

      const { uploadUrl, s3Key, downloadUrl } = await response.json();
      console.log('Got upload URL, uploading to S3...', { s3Key, contentType, fileName: file.name });

      // Upload file to S3 - Content-Type MUST match what was used for presigned URL
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': contentType,
        },
        mode: 'cors',
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text().catch(() => 'No error body');
        console.error('S3 upload failed:', {
          status: uploadResponse.status,
          statusText: uploadResponse.statusText,
          errorText,
          uploadUrl: uploadUrl.substring(0, 100) + '...',
        });
        throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }

      console.log('Upload successful!', { s3Key, downloadUrl });

      // Return the uploaded asset
      onSelect({
        name: file.name,
        s3Key: s3Key,
        url: downloadUrl,
        duration: undefined, // Could probe for duration if needed
      });
      onClose();
    } catch (error) {
      console.error('Upload error:', error);
      alert(`Failed to upload file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [onSelect, onClose]);

  const acceptTypes: Record<string, string[]> = {
    video: ['video/mp4', 'video/quicktime', 'video/webm'],
    image: ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
    audio: ['audio/mpeg', 'audio/wav', 'audio/mp3', 'audio/aac'],
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptTypes[assetType]?.reduce((acc, type) => ({ ...acc, [type]: [] }), {}),
    maxFiles: 1,
    disabled: isUploading,
  });

  const getIcon = () => {
    switch (assetType) {
      case 'video': return <Video className="w-5 h-5" />;
      case 'image': return <Image className="w-5 h-5" />;
      case 'audio': return <Music className="w-5 h-5" />;
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {getIcon()}
            {title}
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-4 mt-4">
          {/* Upload Zone */}
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
              ${isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}
              ${isUploading ? 'pointer-events-none opacity-50' : ''}
            `}
          >
            <input {...getInputProps()} />
            {isUploading ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Uploading...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="w-8 h-8 text-muted-foreground" />
                <p className="text-sm">
                  {isDragActive ? 'Drop file here' : 'Drag & drop or click to upload'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {assetType === 'video' && 'MP4, MOV, WebM'}
                  {assetType === 'image' && 'PNG, JPG, GIF, WebP'}
                  {assetType === 'audio' && 'MP3, WAV, AAC'}
                </p>
              </div>
            )}
          </div>

          {/* Divider */}
          {filteredFiles.length > 0 && (
            <div className="flex items-center gap-3">
              <div className="flex-1 border-t" />
              <span className="text-xs text-muted-foreground">or select from project</span>
              <div className="flex-1 border-t" />
            </div>
          )}

          {/* Project Files */}
          {filteredFiles.length > 0 ? (
            <ScrollArea className="h-[400px]">
              <div className="grid grid-cols-2 gap-3 p-1">
                {filteredFiles.map((file) => (
                  <button
                    key={file.id}
                    onClick={() => handleFileSelect(file)}
                    className="group relative aspect-video rounded-lg overflow-hidden border hover:border-primary transition-colors bg-muted"
                  >
                    {file.thumbnailUrl ? (
                      <img
                        src={file.thumbnailUrl}
                        alt={file.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        {file.fileType === 'video' && <Video className="w-8 h-8 text-muted-foreground" />}
                        {file.fileType === 'image' && <Image className="w-8 h-8 text-muted-foreground" />}
                        {file.fileType === 'audio' && <Music className="w-8 h-8 text-muted-foreground" />}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Check className="w-6 h-6 text-white" />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                      <p className="text-xs text-white truncate">{file.name}</p>
                    </div>
                  </button>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">No {assetType} files in your project.</p>
              <p className="text-xs mt-1">Upload a file above to get started.</p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
